/**
 * GlobalDocumentEditor - Edit documents with live preview.
 *
 * 2026-11-05 improvement pass (audit: document-editor-audit-2026-11-05.md):
 * - The editor's own Save/PDF/PNG buttons were stubs that alerted
 *   success while doing nothing (App passes no save/export props).
 *   They are GONE: HistoryBar is the one save path (browser-local +
 *   account sync via designKind 'globaldoc'), and the export
 *   buttons now generate a real PNG/PDF from the document state at
 *   its real physical size, 300 DPI (utils/documentExport).
 * - The fabricated fraud-risk badge and the "N Editors" badge are
 *   removed, and the team-collaboration wiring is unwired from
 *   this editor: its client socket events match no running server,
 *   so the badge could only ever show a demo constant. (The shared
 *   collaboration code is untouched and can be re-wired if the
 *   protocol is ever repaired end-to-end.)
 * - autoPersistWork is on: a refresh no longer silently loses edits.
 * - Photos are downscaled on ingest and saves carry strip
 *   fallbacks, so one phone photo can no longer blow the storage
 *   quota and fail every save.
 * - The signature pad is rebuilt on pointer events (touch works),
 *   one canvas per field, a fresh path per stroke, sized to the
 *   field's aspect, and it restores the saved signature.
 * - The Design tab is document-native: background colour and
 *   border controls that all actually change the document (the
 *   borrowed check panel's other sections were silently dead here).
 *
 * 2026-11-05 parity pass (phase 2 - the check studio's components
 * in the document editor, per Princewill's directive):
 * - The check studio's kept asset libraries (logos / signatures /
 *   pictures, SAME storage keys) live in a Library tab here: a
 *   signature saved on a check appears here and vice versa.
 *   Tapping a tile places the asset on the document as an ordinary
 *   movable/scalable/deletable object that exports with it.
 * - Signature fields gain the check studio's ink system (presets
 *   incl. Ball-pen blue-black #1b2440, custom colour, Keep
 *   original) and a Signature Lab (draw / upload / type): the
 *   placed value is derived from a stored raw source, so changing
 *   the ink re-derives every signature field exactly - the model
 *   the check side shipped. The pointer pad stays, now drawing in
 *   the picked ink. (Corrective, same day: signatures PLACED from
 *   the library re-derive on an ink pick too - they carry their
 *   library entry's id, and the entry holds the raw source.)
 * - "Refresh my document" is the owner's own-documents surface:
 *   his uploaded PDF/image becomes a background and he edits ON
 *   TOP of it by hand - text boxes, opaque cover patches (digital
 *   white-out), highlights, redaction blocks, library placements,
 *   brightness/contrast/rotate/crop. NOTHING analyses the upload:
 *   no detection, no OCR, no content-aware fill, no automatic
 *   removal, and the upload never goes near the clone engines.
 *   Exports flatten background + overlays at the page's own
 *   resolution, and say so.
 */

import React, { useState, useRef, useEffect } from 'react';
import { UniversalDocumentCloningEngine } from '../engines/UniversalDocumentCloningEngine';
import { BorderlineEngine, BorderConfig } from './BorderlineEngine';
import { useHistorySnapshots } from '../hooks/useHistorySnapshots';
import HistoryBar from './HistoryBar';
import {
    GLOBAL_DOC_TEMPLATES, freshTemplate, mapClonedDocument, DEFAULT_DOC_BORDER,
    DocumentData, EditableField, PlacedDocItem
} from '../utils/globalDocTemplates';
import { downscaleImageFile } from '../utils/imageIngest';
import {
    exportDocumentPdf, exportDocumentPng, DOCUMENT_EXPORT_DPI,
    documentPhysicalSizeMm, isLightColor
} from '../utils/documentExport';
import { AssetLibraryPanel, AssetLibraryPanelHandle } from './AssetLibraryPanel';
import { DocSignatureLab, SignatureLabResult, SignatureInkControls } from './DocSignatureLab';
import { DocumentRefresh } from './DocumentRefresh';
import {
    DEFAULT_SIGNATURE_INK, rederiveSignatureValue, deriveSignatureImage,
    rederivePlacedSignatureItems
} from '../utils/signatureInk';
import {
    RefreshState, createRefreshFromImageFile, createRefreshFromPdfFile, stripRefreshBackground
} from '../utils/refreshDocument';
import { loadAssetLibrary, AssetEntry, AssetKind } from '../utils/assetLibrary';

interface GlobalDocumentEditorProps {
    document?: DocumentData;
    theme?: 'light' | 'dark';
}

const templateFromUrl = (): string | null => {
    try {
        const t = new URLSearchParams(window.location.search).get('template');
        return t && GLOBAL_DOC_TEMPLATES[t] ? t : null;
    } catch { return null; }
};

/** Remove embedded photo (and optionally signature) data URLs from a snapshot for storage fallbacks.
 * Parity pass: a signature's raw source is stripped together with its
 * value (re-derivation needs the value anyway), placed-item images are
 * stripped on the full fallback, and the refresh page background is
 * stripped on the full fallback - geometry and overlays always stay. */
const stripDocImages = (snap: any, keepSignature: boolean): any => {
    if (!snap) return snap;
    let out = snap;
    if (snap.doc && Array.isArray(snap.doc.fields)) {
        out = {
            ...out,
            doc: {
                ...snap.doc,
                fields: snap.doc.fields.map((f: EditableField) => {
                    if (f.type === 'photo') return { ...f, value: '' };
                    if (f.type === 'signature' && !keepSignature) return { ...f, value: '', rawValue: undefined, sigSource: undefined };
                    return f;
                })
            }
        };
    }
    if (!keepSignature) {
        if (Array.isArray(out.placedItems)) {
            out = { ...out, placedItems: out.placedItems.map((p: PlacedDocItem) => (p.kind === 'image' ? { ...p, src: '' } : p)) };
        }
        if (out.refresh) out = { ...out, refresh: stripRefreshBackground(out.refresh) };
    }
    return out;
};

/**
 * Signature pad for ONE field: its own canvas (the old pad shared a
 * single ref across fields), pointer events with capture (touch +
 * mouse; releasing outside the pad still commits), a fresh path per
 * stroke (strokes no longer connect), resolution matched to the
 * field box's aspect, and the saved signature is drawn back in.
 */
const SignaturePad: React.FC<{ field: EditableField; onDrawn: (raw: string) => void; strokeColor: string; borderColor: string }> = ({ field, onDrawn, strokeColor, borderColor }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const drawingRef = useRef(false);
    const drewRef = useRef(false);
    const lastRef = useRef<{ x: number; y: number } | null>(null);
    const W = 320;
    const H = Math.max(48, Math.round((W * field.position.height) / Math.max(1, field.position.width)));

    // Restore the saved signature into the pad (and clear when the value is cleared).
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || drawingRef.current) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (field.value) {
            const img = new Image();
            img.onload = () => { ctx.drawImage(img, 0, 0, canvas.width, canvas.height); };
            img.src = field.value;
        }
    }, [field.value, H]);

    const point = (e: React.PointerEvent): { x: number; y: number } => {
        const canvas = canvasRef.current!;
        const r = canvas.getBoundingClientRect();
        return {
            x: (e.clientX - r.left) * (canvas.width / Math.max(1, r.width)),
            y: (e.clientY - r.top) * (canvas.height / Math.max(1, r.height))
        };
    };

    const strokeTo = (e: React.PointerEvent) => {
        const canvas = canvasRef.current;
        if (!canvas || !drawingRef.current) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const p = point(e);
        const last = lastRef.current || p;
        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(p.x, p.y);
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
        lastRef.current = p;
        drewRef.current = true;
    };

    const endStroke = () => {
        if (!drawingRef.current) return;
        drawingRef.current = false;
        lastRef.current = null;
        const canvas = canvasRef.current;
        if (canvas && drewRef.current) {
            drewRef.current = false;
            try { onDrawn(canvas.toDataURL('image/png')); } catch { /* canvas unavailable: keep the old value */ }
        }
    };

    return (
        <div>
            <div style={{ border: `1px solid ${borderColor}`, borderRadius: '6px', background: '#FFF', marginBottom: '4px', overflow: 'hidden' }}>
                <canvas
                    ref={canvasRef}
                    data-signature-pad={field.id}
                    width={W}
                    height={H}
                    style={{ width: '100%', display: 'block', cursor: 'crosshair', touchAction: 'none' }}
                    onPointerDown={e => {
                        try { (e.target as HTMLElement).setPointerCapture(e.pointerId); } catch { /* capture is best-effort */ }
                        drawingRef.current = true;
                        lastRef.current = point(e);
                        strokeTo(e);
                    }}
                    onPointerMove={strokeTo}
                    onPointerUp={endStroke}
                    onPointerCancel={endStroke}
                />
            </div>
            <button
                type="button"
                onClick={() => {
                    const canvas = canvasRef.current;
                    const ctx = canvas?.getContext('2d');
                    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
                    onDrawn('');
                }}
                style={{ padding: '3px 6px', fontSize: '10px', background: 'transparent', border: `1px solid ${borderColor}`, borderRadius: '4px', color: '#6B7280', cursor: 'pointer' }}
            >
                Clear
            </button>
        </div>
    );
};

export const GlobalDocumentEditor: React.FC<GlobalDocumentEditorProps> = ({ document: initial, theme = 'light' }) => {
    const urlTemplate = templateFromUrl();
    const [activeTab, setActiveTab] = useState<string>('fields');
    const [activeTemplate, setActiveTemplate] = useState<string>(urlTemplate || 'us_passport');
    const [doc, setDoc] = useState<DocumentData>(() => initial || freshTemplate(urlTemplate || 'us_passport') || GLOBAL_DOC_TEMPLATES.us_passport);
    const [borderConfig, setBorderConfig] = useState<BorderConfig>({ ...DEFAULT_DOC_BORDER });
    const [selectedField, setSelectedField] = useState<string | null>(null);
    const [exportBusy, setExportBusy] = useState(false);
    const [exportNote, setExportNote] = useState<string | null>(null);
    // --- Parity state (phase 2): shared libraries, the ink system,
    // placed objects, and the refresh workspace. All of it rides the
    // same history snapshot, so undo/save/restore cover it too. ---
    const [signatureInkColor, setSignatureInkColor] = useState<string>(DEFAULT_SIGNATURE_INK);
    const [signatureInkOriginal, setSignatureInkOriginal] = useState<boolean>(false);
    const [placedItems, setPlacedItems] = useState<PlacedDocItem[]>([]);
    const [selectedPlacedId, setSelectedPlacedId] = useState<string | null>(null);
    const [refresh, setRefresh] = useState<RefreshState | null>(null);
    const [labFieldId, setLabFieldId] = useState<string | null>(null);
    const libraryPanelRef = useRef<AssetLibraryPanelHandle>(null);
    const refreshUploadRef = useRef<HTMLInputElement>(null);
    const inRefresh = refresh !== null;

    // --- Design history: the ONE save path (local + account) ---
    const designHistory = useHistorySnapshots({
        storageKey: 'oc-globaldoc-design',
        designKind: 'globaldoc',
        designName: () => `${(doc as any)?.name || 'Document design'}`,
        autoPersistWork: true,
        stripForWorkSave: (s: any) => stripDocImages(s, true),
        stripForSave: (s: any) => stripDocImages(s, false),
        snapshot: () => ({ doc, borderConfig, activeTemplate, placedItems, refresh, signatureInkColor, signatureInkOriginal }),
        apply: (s: any) => {
            if (!s) return;
            if ('activeTemplate' in s) setActiveTemplate(s.activeTemplate);
            if ('borderConfig' in s) setBorderConfig(s.borderConfig);
            if ('doc' in s) setDoc(s.doc);
            setPlacedItems(Array.isArray(s.placedItems) ? s.placedItems : []);
            setRefresh(s.refresh || null);
            if (typeof s.signatureInkColor === 'string') setSignatureInkColor(s.signatureInkColor);
            if (typeof s.signatureInkOriginal === 'boolean') setSignatureInkOriginal(s.signatureInkOriginal);
            setSelectedPlacedId(null);
        },
        watch: [doc, borderConfig, activeTemplate, placedItems, refresh, signatureInkColor, signatureInkOriginal]
    });

    // Gallery hand-off: /document-editor?template=<id> loads that
    // template as the current design (over anything restored),
    // exactly once, and the parameter is then dropped so a later
    // refresh continues the user's edits instead of resetting them.
    const urlTemplateApplied = useRef(false);
    useEffect(() => {
        if (urlTemplateApplied.current) return;
        urlTemplateApplied.current = true;
        if (!urlTemplate) return;
        const fresh = freshTemplate(urlTemplate);
        if (!fresh) return;
        designHistory.restore({ doc: fresh, borderConfig: { ...DEFAULT_DOC_BORDER }, activeTemplate: urlTemplate });
        setSelectedField(null);
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('template');
            window.history.replaceState(null, '', url.toString());
        } catch { /* the parameter staying is cosmetic */ }
    }, []);

    // Engine instance (protected cloning engine, used as a black box)
    const cloningEngine = useRef(new UniversalDocumentCloningEngine());

    // Freshest doc for async handlers (ink re-derivation reads the
    // raw sources as they are when the pick happens).
    const docRef = useRef(doc);
    docRef.current = doc;
    // Same for placed items, plus a sequence stamp so a slower
    // earlier ink pick can never overwrite a newer pick's result.
    const placedItemsRef = useRef(placedItems);
    placedItemsRef.current = placedItems;
    const inkPickSeqRef = useRef(0);

    const colors = { bg: theme === 'dark' ? '#111827' : '#F9FAFB', card: theme === 'dark' ? '#1F2937' : '#FFFFFF', text: theme === 'dark' ? '#FFF' : '#111827', textSecondary: theme === 'dark' ? '#9CA3AF' : '#6B7280', border: theme === 'dark' ? '#374151' : '#E5E7EB', primary: '#3B82F6', success: '#10B981', danger: '#EF4444', warning: '#F59E0B' };

    const loadTemplate = (id: string) => {
        const fresh = freshTemplate(id);
        if (fresh) { setDoc(fresh); setActiveTemplate(id); setSelectedField(null); setExportNote(null); setPlacedItems([]); setSelectedPlacedId(null); setRefresh(null); }
    };

    const updateField = (fieldId: string, value: string) => {
        setDoc(prev => ({ ...prev, fields: prev.fields.map(f => f.id === fieldId ? { ...f, value } : f) }));
    };

    // --- Signature ink: one ink drives every signature on the
    // document. A pick re-derives each signature FIELD from its
    // stored raw source (typed signatures re-render from their
    // text) - the check studio's promise, kept here too. Fields
    // with no raw source (legacy values) are left exactly as they
    // are. A pick ALSO re-derives every signature PLACED from the
    // library (parity corrective, 2026-11-05): the Library tab's
    // copy says changing the ink re-inks them all, and placed
    // signatures were the ones it did not reach. A placed item
    // stores only its derived image and its library entry's id;
    // the entry holds the raw source the derivation re-runs from.
    // Placed logos/pictures never re-ink, and a placed signature
    // whose entry is gone (or whose image a stripped save did not
    // keep) stays exactly as it is. ---
    const handleInkChange = async (color: string, original: boolean) => {
        setSignatureInkColor(color);
        setSignatureInkOriginal(original);
        const seq = ++inkPickSeqRef.current;
        const current = docRef.current;
        const sigFields = current.fields.filter(f => f.type === 'signature' && (f.rawValue || f.sigSource));
        if (sigFields.length) {
            const updates = new Map<string, string>();
            for (const f of sigFields) {
                const next = await rederiveSignatureValue(f, color, original);
                if (next) updates.set(f.id, next);
            }
            if (updates.size) {
                setDoc(prev => ({ ...prev, fields: prev.fields.map(f => (updates.has(f.id) ? { ...f, value: updates.get(f.id)! } : f)) }));
            }
        }
        const placed = placedItemsRef.current;
        if (placed.some(p => p.kind === 'image' && p.assetKind === 'signatures' && p.assetId && p.src)) {
            const nextPlaced = await rederivePlacedSignatureItems(
                placed,
                assetId => loadAssetLibrary('signatures').find(e => e.id === assetId)?.src,
                color,
                original
            );
            // Only the latest pick writes: an earlier pick whose
            // derivation resolved late must not repaint over it.
            if (nextPlaced !== placed && inkPickSeqRef.current === seq) setPlacedItems(nextPlaced);
        }
    };

    /** The pad drew (or cleared): the drawing is the raw source.
     * A pad drawing is already isolated strokes in the pad ink, so
     * it places as drawn immediately; the shared derivation then
     * refines it (background cleanup / exact ink) when it resolves,
     * guarded so a newer drawing is never overwritten. */
    const commitPadSignature = async (fieldId: string, raw: string) => {
        if (!raw) {
            setDoc(prev => ({ ...prev, fields: prev.fields.map(f => f.id === fieldId ? { ...f, value: '', rawValue: undefined, sigSource: undefined } : f) }));
            return;
        }
        setDoc(prev => ({ ...prev, fields: prev.fields.map(f => f.id === fieldId ? { ...f, value: raw, rawValue: raw, sigSource: undefined } : f) }));
        const derived = await deriveSignatureImage(raw, signatureInkColor, signatureInkOriginal);
        if (derived && derived !== raw) {
            setDoc(prev => ({ ...prev, fields: prev.fields.map(f => (f.id === fieldId && f.rawValue === raw ? { ...f, value: derived } : f)) }));
        }
    };

    /** A kept signature from the shared library, set into a field. */
    const useLibrarySignature = async (fieldId: string, entry: AssetEntry) => {
        const derived = await deriveSignatureImage(entry.src, signatureInkColor, signatureInkOriginal);
        setDoc(prev => ({ ...prev, fields: prev.fields.map(f => f.id === fieldId ? { ...f, value: derived, rawValue: entry.src, sigSource: undefined } : f) }));
    };

    /** The Signature Lab saved: fill the target field, and keep the
     * signature in the shared library so it is there next time -
     * here and in the check studio. */
    const handleLabSave = (result: SignatureLabResult) => {
        libraryPanelRef.current?.addExternal('signatures', result.name, result.raw);
        if (!labFieldId) return;
        const fieldId = labFieldId;
        setDoc(prev => ({
            ...prev,
            fields: prev.fields.map(f => f.id === fieldId ? {
                ...f,
                value: result.derived,
                rawValue: result.raw,
                sigSource: result.typed ? { kind: 'typed' as const, text: result.typed.text, face: result.typed.face, ink: result.typed.ink } : undefined
            } : f)
        }));
    };

    // --- Placed library objects on template documents ---
    const placeLibraryItem = async (kind: AssetKind, entry: AssetEntry) => {
        if (inRefresh || !entry.src) return;
        const src = kind === 'signatures'
            ? await deriveSignatureImage(entry.src, signatureInkColor, signatureInkOriginal)
            : entry.src;
        const probe = new Image();
        const finish = (nw: number, nh: number) => {
            const maxW = doc.layout.width * 0.4;
            const scale = nw > 0 ? Math.min(1, maxW / nw) : 1;
            const w = Math.max(24, Math.round((nw || 160) * scale));
            const h = Math.max(16, Math.round((nh || 60) * scale));
            const item: PlacedDocItem = {
                id: `placed-${Date.now().toString(36)}-${placedItems.length}`,
                kind: 'image', src, assetId: entry.id, assetKind: kind,
                position: { x: Math.round((doc.layout.width - w) / 2), y: Math.round((doc.layout.height - h) / 2), width: w, height: h }
            };
            setPlacedItems(prev => [...prev, item]);
            setSelectedPlacedId(item.id);
        };
        probe.onload = () => finish(probe.naturalWidth || probe.width, probe.naturalHeight || probe.height);
        probe.onerror = () => finish(160, 60);
        probe.src = src;
    };

    const placedAssetIds = new Set<string>([
        ...placedItems.map(p => p.assetId).filter(Boolean) as string[],
        ...(refresh ? refresh.overlays.filter(o => o.kind === 'image').map(o => o.id) : [])
    ]);

    // --- Refresh surface: the owner's own uploaded page. The file
    // becomes a BACKGROUND via the refresh ingest only - it never
    // goes through the clone engine or any analysis. ---
    const handleRefreshUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        try {
            const state = file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
                ? await createRefreshFromPdfFile(file)
                : await createRefreshFromImageFile(file);
            setRefresh(state);
            setExportNote(null);
        } catch (err) {
            console.error('Refresh upload failed:', err);
            alert('That file could not be read as a PDF or an image. Your current document was left untouched.');
        }
    };

    const handleCloneUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        try {
            const cloned = await cloningEngine.current.cloneDocument(file);
            const mapped = mapClonedDocument(cloned);
            setDoc(mapped);
            setActiveTemplate(mapped.id);
            setSelectedField(null);
            setExportNote(null);
        } catch (err) {
            console.error('Cloning failed:', err);
            alert('Document cloning failed. Please ensure the image is clear.');
        }
    };

    const formatSSN = (v: string) => { const d = v.replace(/\D/g, '').slice(0, 9); if (d.length >= 5) return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`; if (d.length >= 3) return `${d.slice(0, 3)}-${d.slice(3)}`; return d; };

    const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>, fieldId: string) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        // Downscale on ingest: saves stop failing on storage quota
        // and undo snapshots stop carrying multi-MB data URLs.
        const dataUrl = await downscaleImageFile(file);
        updateField(fieldId, dataUrl);
    };

    const handleExport = async (fmt: 'pdf' | 'png') => {
        if (exportBusy) return;
        setExportBusy(true);
        setExportNote(null);
        try {
            const r = fmt === 'pdf'
                ? await exportDocumentPdf(doc, borderConfig, DOCUMENT_EXPORT_DPI, undefined, placedItems)
                : await exportDocumentPng(doc, borderConfig, DOCUMENT_EXPORT_DPI, undefined, placedItems);
            setExportNote(`Exported ${fmt.toUpperCase()} — ${r.sizeMm.w} × ${r.sizeMm.h} mm at ${r.dpi} DPI (${r.widthPx} × ${r.heightPx} px)`);
        } catch (err) {
            console.error('Document export failed:', err);
            setExportNote('Export failed — the file could not be generated in this browser. Nothing was downloaded.');
        } finally {
            setExportBusy(false);
        }
    };

    const isComplete = doc.fields.filter(f => f.required).every(f => f.value);
    const physical = documentPhysicalSizeMm(doc);

    // --- Scale-to-fit preview stage ---
    const stageRef = useRef<HTMLDivElement>(null);
    const [stageScale, setStageScale] = useState(1);
    useEffect(() => {
        const el = stageRef.current;
        if (!el) return;
        const measure = () => {
            const w = el.clientWidth;
            if (w > 0) setStageScale(Math.min(1, w / Math.max(1, doc.layout.width)));
        };
        measure();
        const RO = (window as any).ResizeObserver;
        if (!RO) return;
        const ro = new RO(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [doc.layout.width]);

    const fieldInk = (f: EditableField): string => f.style?.color || (isLightColor(doc.layout.backgroundColor) ? '#000' : '#FFF');

    return (
        <div style={{ background: colors.bg, borderRadius: '16px', padding: '24px', minHeight: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', margin: '0 0 20px' }}>
                <div>
                    <h2 style={{ margin: '0 0 4px', color: colors.text, fontSize: '20px', fontWeight: '700' }}>🌍 Global Document Editor</h2>
                    <p style={{ margin: 0, color: colors.textSecondary, fontSize: '13px' }}>Edit passports, IDs, SSN cards from any country</p>
                </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
                <HistoryBar history={designHistory} label="Design" />
            </div>

            {/* Template Select */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                {Object.entries(GLOBAL_DOC_TEMPLATES).map(([id, t]) => (
                    <button key={id} onClick={() => loadTemplate(id)} style={{ padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', background: activeTemplate === id ? colors.primary : colors.card, color: activeTemplate === id ? '#FFF' : colors.text, border: `1px solid ${activeTemplate === id ? colors.primary : colors.border}`, fontSize: '12px', fontWeight: '500' }}>
                        {t.category === 'passport' ? '🛂' : t.category === 'ssn_card' ? '📋' : t.category === 'national_id' ? '🆔' : '🪪'} {t.name}
                    </button>
                ))}
                <div style={{ height: '24px', width: '1px', background: colors.border, margin: '0 8px' }} />
                <input type="file" id="clone-upload" style={{ display: 'none' }} onChange={handleCloneUpload} accept="image/*" />
                <button onClick={() => document.getElementById('clone-upload')?.click()} style={{ padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', background: colors.card, color: colors.primary, border: `1px solid ${colors.primary}`, fontSize: '12px', fontWeight: '600' }}>
                    🧬 Clone Existing Document
                </button>
                <input ref={refreshUploadRef} type="file" id="refresh-upload" style={{ display: 'none' }} onChange={handleRefreshUpload} accept="image/*,application/pdf" />
                <button onClick={() => refreshUploadRef.current?.click()} style={{ padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', background: colors.card, color: colors.primary, border: `1px solid ${colors.primary}`, fontSize: '12px', fontWeight: '600' }}>
                    🔄 Refresh My Own Document
                </button>
            </div>

            {inRefresh && refresh ? (
                <DocumentRefresh state={refresh} onChange={setRefresh} onExit={() => setRefresh(null)} colors={colors} />
            ) : (
            <>
            <div className="oc-g2" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '20px' }}>
                {/* Preview */}
                <div style={{ background: colors.card, borderRadius: '12px', padding: '20px', border: `1px solid ${colors.border}` }}>
                    <h3 style={{ margin: '0 0 12px', color: colors.text, fontSize: '14px' }}>📄 Preview</h3>
                    <div ref={stageRef} style={{ width: '100%', height: doc.layout.height * stageScale, position: 'relative', overflow: 'hidden' }}>
                        <div style={{ width: doc.layout.width, height: doc.layout.height, margin: '0 auto', transform: `scale(${stageScale})`, transformOrigin: 'top center' }}>
                            <div data-doc-preview style={{ background: doc.layout.backgroundColor, width: doc.layout.width, height: doc.layout.height, borderRadius: '8px', position: 'relative', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
                                <svg width={doc.layout.width} height={doc.layout.height} style={{ position: 'absolute', top: 0, left: 0, zIndex: 0, pointerEvents: 'none' }}>
                                    <BorderlineEngine width={doc.layout.width} height={doc.layout.height} config={borderConfig} />
                                </svg>

                                {doc.fields.map(f => (
                                    <div key={f.id} onClick={() => setSelectedField(f.id)} style={{
                                        position: 'absolute', left: f.position.x, top: f.position.y, width: f.position.width, height: f.position.height,
                                        background: f.type === 'photo' ? (f.value ? `url(${f.value}) center/cover` : '#E5E7EB') : f.type === 'signature' ? (f.value ? `url(${f.value}) center/contain no-repeat` : '#F3F4F6') : 'transparent',
                                        border: selectedField === f.id ? '2px solid #3B82F6' : '1px dashed rgba(255,255,255,0.3)', borderRadius: '2px', cursor: 'pointer',
                                        fontSize: f.style?.fontSize || 11, fontWeight: f.style?.fontWeight || 'normal', color: fieldInk(f),
                                        textTransform: (f.style?.textTransform as any) || 'none', display: 'flex', alignItems: 'center', paddingLeft: ['photo', 'signature'].includes(f.type) ? 0 : '4px', overflow: 'hidden'
                                    }}>
                                        {!['photo', 'signature'].includes(f.type) && (f.value || <span style={{ opacity: 0.5, fontSize: 10 }}>{f.label}</span>)}
                                        {f.type === 'photo' && !f.value && <span style={{ margin: 'auto', fontSize: 20 }}>📷</span>}
                                        {f.type === 'signature' && !f.value && <span style={{ margin: 'auto', fontSize: 9, color: '#999' }}>Sign</span>}
                                    </div>
                                ))}

                                {placedItems.map(p => (
                                    <div
                                        key={p.id}
                                        data-placed-item={p.id}
                                        onPointerDown={e => {
                                            e.stopPropagation();
                                            setSelectedPlacedId(p.id);
                                            const startX = e.clientX, startY = e.clientY;
                                            const orig = { ...p.position };
                                            const move = (ev: PointerEvent) => {
                                                const dx = (ev.clientX - startX) / Math.max(0.05, stageScale);
                                                const dy = (ev.clientY - startY) / Math.max(0.05, stageScale);
                                                setPlacedItems(prev => prev.map(it => it.id === p.id ? {
                                                    ...it,
                                                    position: {
                                                        ...it.position,
                                                        x: Math.min(doc.layout.width - 16, Math.max(0, Math.round(orig.x + dx))),
                                                        y: Math.min(doc.layout.height - 12, Math.max(0, Math.round(orig.y + dy)))
                                                    }
                                                } : it));
                                            };
                                            const up = () => {
                                                window.removeEventListener('pointermove', move);
                                                window.removeEventListener('pointerup', up);
                                            };
                                            window.addEventListener('pointermove', move);
                                            window.addEventListener('pointerup', up);
                                        }}
                                        style={{
                                            position: 'absolute', left: p.position.x, top: p.position.y, width: p.position.width, height: p.position.height,
                                            cursor: 'move', touchAction: 'none', zIndex: 3,
                                            outline: selectedPlacedId === p.id ? '2px solid #3B82F6' : '1px dashed rgba(59,130,246,0.4)'
                                        }}
                                    >
                                        {p.kind === 'image' && p.src
                                            ? <img src={p.src} alt="Placed from your library" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none', display: 'block' }} />
                                            : p.kind === 'image'
                                                ? <span style={{ fontSize: '9px', color: '#999', display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2px' }}>image not kept in this save</span>
                                                : <span style={{ fontSize: '11px', pointerEvents: 'none' }}>{p.text}</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Fields / Design */}
                <div style={{ background: colors.card, borderRadius: '12px', padding: '16px', border: `1px solid ${colors.border}`, maxHeight: '500px', overflowY: 'auto' }}>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '8px' }}>
                        <button onClick={() => setActiveTab('fields')} style={{ background: 'none', border: 'none', borderBottom: activeTab === 'fields' ? `2px solid ${colors.primary}` : 'none', color: activeTab === 'fields' ? colors.primary : colors.textSecondary, padding: '4px 8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>📝 Fields</button>
                        <button onClick={() => setActiveTab('design')} style={{ background: 'none', border: 'none', borderBottom: activeTab === 'design' ? `2px solid ${colors.primary}` : 'none', color: activeTab === 'design' ? colors.primary : colors.textSecondary, padding: '4px 8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>🎨 Design</button>
                        <button onClick={() => setActiveTab('library')} style={{ background: 'none', border: 'none', borderBottom: activeTab === 'library' ? `2px solid ${colors.primary}` : 'none', color: activeTab === 'library' ? colors.primary : colors.textSecondary, padding: '4px 8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>📁 Library</button>
                    </div>

                    {activeTab === 'fields' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {doc.fields.map(f => (
                                <div key={f.id}>
                                    <label style={{ display: 'block', color: colors.text, fontSize: '11px', fontWeight: '500', marginBottom: '3px' }}>{f.label}{f.required && <span style={{ color: '#EF4444' }}> *</span>}</label>
                                    {f.type === 'select' ? (
                                        <select value={f.value} onChange={e => updateField(f.id, e.target.value)} style={{ width: '100%', padding: '7px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: '12px' }}>
                                            {f.options?.map(o => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    ) : f.type === 'photo' ? (
                                        <>
                                            <input type="file" accept="image/*" onChange={e => handlePhotoUpload(e, f.id)} id={`p-${f.id}`} style={{ display: 'none' }} />
                                            <button onClick={() => document.getElementById(`p-${f.id}`)?.click()} style={{ width: '100%', padding: '7px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, cursor: 'pointer', fontSize: '12px' }}>{f.value ? '✅ Photo' : '📤 Upload'}</button>
                                        </>
                                    ) : f.type === 'signature' ? (
                                        <div>
                                            <SignaturePad
                                                field={f}
                                                borderColor={colors.border}
                                                strokeColor={signatureInkOriginal ? '#111827' : signatureInkColor}
                                                onDrawn={raw => { void commitPadSignature(f.id, raw); }}
                                            />
                                            <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setLabFieldId(f.id)}
                                                    style={{ padding: '4px 8px', fontSize: '10px', fontWeight: '600', background: colors.primary, border: 'none', borderRadius: '4px', color: '#FFF', cursor: 'pointer' }}
                                                >
                                                    ✒️ Signature Lab
                                                </button>
                                                {loadAssetLibrary('signatures').length > 0 && (
                                                    <select
                                                        aria-label={`Use a kept signature for ${f.label}`}
                                                        value=""
                                                        onChange={e => {
                                                            const entry = loadAssetLibrary('signatures').find(en => en.id === e.target.value);
                                                            if (entry) void useLibrarySignature(f.id, entry);
                                                        }}
                                                        style={{ flex: 1, minWidth: '120px', padding: '4px', fontSize: '10px', borderRadius: '4px', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text }}
                                                    >
                                                        <option value="">Use a kept signature…</option>
                                                        {loadAssetLibrary('signatures').map(en => <option key={en.id} value={en.id}>{en.name}</option>)}
                                                    </select>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <input type={f.type === 'date' ? 'date' : 'text'} value={f.value} onChange={e => updateField(f.id, f.type === 'ssn' ? formatSSN(e.target.value) : e.target.value)} placeholder={f.label} style={{ width: '100%', padding: '7px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: '12px' }} />
                                    )}
                                </div>
                            ))}
                            {(() => {
                                const sel = placedItems.find(p => p.id === selectedPlacedId);
                                if (!sel) return null;
                                return (
                                    <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '10px' }}>
                                        <label style={{ display: 'block', color: colors.text, fontSize: '11px', fontWeight: '500', marginBottom: '3px' }}>Placed item - drag it on the document to move it</label>
                                        <label style={{ display: 'block', color: colors.textSecondary, fontSize: '11px', marginBottom: '3px' }}>Width - {sel.position.width}</label>
                                        <input
                                            type="range" min={24} max={doc.layout.width} step={1} aria-label="Placed item width"
                                            value={sel.position.width}
                                            onChange={e => {
                                                const w = Number(e.target.value);
                                                setPlacedItems(prev => prev.map(it => it.id === sel.id ? { ...it, position: { ...it.position, width: w, height: Math.max(12, Math.round(it.position.height * (w / Math.max(1, it.position.width)))) } } : it));
                                            }}
                                            style={{ width: '100%' }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => { setPlacedItems(prev => prev.filter(it => it.id !== sel.id)); setSelectedPlacedId(null); }}
                                            style={{ marginTop: '6px', padding: '5px 10px', fontSize: '11px', fontWeight: '600', background: 'transparent', border: `1px solid ${colors.danger}`, borderRadius: '6px', color: colors.danger, cursor: 'pointer' }}
                                        >
                                            Remove placed item
                                        </button>
                                    </div>
                                );
                            })()}
                        </div>
                    ) : activeTab === 'library' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <div>
                                <label style={{ display: 'block', color: colors.text, fontSize: '11px', fontWeight: '500', marginBottom: '3px' }}>Signature ink</label>
                                <SignatureInkControls inkColor={signatureInkColor} inkOriginal={signatureInkOriginal} onInkChange={handleInkChange} dark={theme === 'dark'} />
                                <p style={{ margin: '6px 0 0', color: colors.textSecondary, fontSize: '11px' }}>One ink for every signature on this document - changing it re-inks them all.</p>
                            </div>
                            <AssetLibraryPanel
                                ref={libraryPanelRef}
                                colors={colors}
                                placedAssetIds={placedAssetIds}
                                onPlace={placeLibraryItem}
                            />
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <p style={{ margin: 0, color: colors.textSecondary, fontSize: '12px' }}>Every control here changes the document — in the preview and in the export.</p>
                            <div>
                                <label style={{ display: 'block', color: colors.text, fontSize: '11px', fontWeight: '500', marginBottom: '3px' }}>Background colour</label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="color"
                                        aria-label="Background colour"
                                        value={/^#[0-9a-f]{6}$/i.test(doc.layout.backgroundColor) ? doc.layout.backgroundColor : '#ffffff'}
                                        onChange={e => setDoc(prev => ({ ...prev, layout: { ...prev.layout, backgroundColor: e.target.value } }))}
                                        style={{ width: '44px', height: '32px', padding: 0, border: `1px solid ${colors.border}`, borderRadius: '6px', background: colors.bg, cursor: 'pointer' }}
                                    />
                                    <span style={{ color: colors.textSecondary, fontSize: '12px' }}>{doc.layout.backgroundColor}</span>
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', color: colors.text, fontSize: '11px', fontWeight: '500', marginBottom: '3px' }}>Border style</label>
                                <select
                                    aria-label="Border style"
                                    value={borderConfig.style}
                                    onChange={e => setBorderConfig(prev => ({ ...prev, style: e.target.value as BorderConfig['style'] }))}
                                    style={{ width: '100%', padding: '7px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: '12px' }}
                                >
                                    <option value="NONE">None</option>
                                    <option value="SOLID">Solid</option>
                                    <option value="DOUBLE">Double</option>
                                    <option value="DOTTED">Dotted</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', color: colors.text, fontSize: '11px', fontWeight: '500', marginBottom: '3px' }}>Border colour</label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="color"
                                        aria-label="Border colour"
                                        value={/^#[0-9a-f]{6}$/i.test(borderConfig.color) ? borderConfig.color : '#000000'}
                                        onChange={e => setBorderConfig(prev => ({ ...prev, color: e.target.value }))}
                                        style={{ width: '44px', height: '32px', padding: 0, border: `1px solid ${colors.border}`, borderRadius: '6px', background: colors.bg, cursor: 'pointer' }}
                                    />
                                    <span style={{ color: colors.textSecondary, fontSize: '12px' }}>{borderConfig.color}</span>
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', color: colors.text, fontSize: '11px', fontWeight: '500', marginBottom: '3px' }}>Border thickness — {borderConfig.thickness}</label>
                                <input
                                    type="range" min={0} max={10} step={1} aria-label="Border thickness"
                                    value={borderConfig.thickness}
                                    onChange={e => setBorderConfig(prev => ({ ...prev, thickness: Number(e.target.value) }))}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', color: colors.text, fontSize: '11px', fontWeight: '500', marginBottom: '3px' }}>Corner radius — {borderConfig.cornerRadius}</label>
                                <input
                                    type="range" min={0} max={40} step={1} aria-label="Corner radius"
                                    value={borderConfig.cornerRadius}
                                    onChange={e => setBorderConfig(prev => ({ ...prev, cornerRadius: Number(e.target.value) }))}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            </>
            )}

            {/* Actions: exports are real files; saving lives in the HistoryBar above (the one save path). */}
            {!inRefresh && (
            <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button onClick={() => handleExport('pdf')} disabled={exportBusy} style={{ padding: '10px 16px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: colors.card, color: colors.text, cursor: exportBusy ? 'wait' : 'pointer', fontSize: '12px' }}>📄 PDF</button>
                    <button onClick={() => handleExport('png')} disabled={exportBusy} style={{ padding: '10px 16px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: colors.card, color: colors.text, cursor: exportBusy ? 'wait' : 'pointer', fontSize: '12px' }}>🖼️ PNG</button>
                    <span style={{ color: colors.textSecondary, fontSize: '11px' }}>Real size {physical.w} × {physical.h} mm · {DOCUMENT_EXPORT_DPI} DPI</span>
                </div>
                <span style={{ color: colors.textSecondary, fontSize: '11px' }}>💾 Save with “Save Design” above — it keeps this document in this browser and, when you are signed in, in your account.</span>
            </div>
            {exportNote && (
                <div style={{ marginTop: '10px', padding: '8px 10px', borderRadius: '8px', background: `${colors.border}50`, color: colors.text, fontSize: '12px' }}>{exportNote}</div>
            )}

            <div style={{ marginTop: '12px', padding: '10px', borderRadius: '8px', background: isComplete ? `${colors.success}15` : `${colors.border}50`, textAlign: 'center' }}>
                <span style={{ color: isComplete ? colors.success : colors.textSecondary, fontSize: '12px' }}>{isComplete ? '✅ Complete' : `⏳ ${doc.fields.filter(f => f.required && !f.value).length} required fields`}</span>
            </div>
            </>
            )}

            <DocSignatureLab
                isOpen={labFieldId !== null}
                onClose={() => setLabFieldId(null)}
                onSave={handleLabSave}
                inkColor={signatureInkColor}
                inkOriginal={signatureInkOriginal}
                onInkChange={handleInkChange}
            />
        </div>
    );
};

export default GlobalDocumentEditor;
